import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Popconfirm,
  Alert,
  Card,
  Table,
  Tag,
  Tooltip,
  Typography,
  Space,
  Button,
  Modal,
  Input,
  Statistic,
  Row,
  Col,
  App,
  Tabs,
  type TableColumnsType,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { PayoutHistory } from './PayoutHistory';
import { downloadPayoutWorkbook } from './payoutExport';
import { ActorCell, ListenerCell, PayToCell, rupees, type AdminPayoutRow } from './payoutViews';

type PendingPayout = AdminPayoutRow;

export function PayoutsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [rejecting, setRejecting] = useState<PendingPayout | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [paying, setPaying] = useState<PendingPayout | null>(null);
  const [payRef, setPayRef] = useState('');
  const [fy, setFy] = useState(currentFy());
  // ?tab=history is the sidebar's "Listener payouts"; no param = the work queue.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'history' ? 'history' : 'queue';
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'payouts', 'pending'],
    queryFn: async () => (await api.get<PendingPayout[]>('/admin/payouts/pending')).data,
  });

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/admin/payouts/${id}/approve`),
    onSuccess: () => {
      message.success('Approved — transfer the money, then mark it paid');
      void qc.invalidateQueries({ queryKey: ['admin', 'payouts'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'console', 'attention'] });
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Could not approve';
      message.error(msg);
    },
  });

  const markPaid = useMutation({
    mutationFn: ({ id, paymentReference }: { id: string; paymentReference: string }) =>
      api.post(`/admin/payouts/${id}/mark-paid`, { paymentReference: paymentReference || undefined }),
    onSuccess: () => {
      message.success('Marked as paid');
      void qc.invalidateQueries({ queryKey: ['admin', 'payouts'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'console', 'attention'] });
      setPaying(null);
      setPayRef('');
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Could not mark paid';
      message.error(msg);
    },
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/admin/payouts/${id}/reject`, { reason }),
    onSuccess: () => {
      message.success('Payout reversed');
      void qc.invalidateQueries({ queryKey: ['admin', 'payouts'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'console', 'attention'] });
      setRejecting(null);
      setRejectReason('');
    },
    onError: () => message.error('Could not reject'),
  });

  async function downloadTdsCsv() {
    try {
      const res = await api.get(`/admin/reports/tds`, {
        params: { fy },
        responseType: 'blob',
      });
      const blob = new Blob([res.data as BlobPart], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tds-${fy}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Could not download TDS report');
    }
  }

  const [exportingQueue, setExportingQueue] = useState(false);

  /** The work queue as shown (after the search box) — it is already loaded whole. */
  async function exportQueue() {
    setExportingQueue(true);
    try {
      const stamp = new Date().toLocaleDateString('en-CA');
      await downloadPayoutWorkbook(
        queue,
        `Work queue — pending review and approved, not yet paid · exported ${new Date().toLocaleString()}`,
        `payout-work-queue_${stamp}.xlsx`,
      );
    } catch {
      message.error('Could not export the payouts');
    } finally {
      setExportingQueue(false);
    }
  }

  const total = (data ?? []).reduce((acc, p) => acc + BigInt(p.amountPaise), 0n);
  // The queue arrives whole (never cut off); search narrows it by listener.
  const queue = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data ?? [];
    return (data ?? []).filter(
      (p) =>
        (p.listener.displayName ?? '').toLowerCase().includes(term) ||
        p.listener.user.handle.toLowerCase().includes(term),
    );
  }, [data, q]);

  const columns: TableColumnsType<PendingPayout> = [
    {
      title: 'Listener',
      render: (_: unknown, row) => <ListenerCell row={row} />,
    },
    {
      title: 'Amount requested',
      dataIndex: 'amountPaise',
      render: (v: string) => rupees(v),
      sorter: (a, b) => Number(BigInt(a.amountPaise) - BigInt(b.amountPaise)),
    },
    {
      title: (
        <Tooltip title="Withheld once when the listener requests the payout (already inside Gross, removed from Net). Shown for reference through approval — it is NOT deducted again at approval or payment.">
          TDS&nbsp;ⓘ
        </Tooltip>
      ),
      dataIndex: 'tdsPaise',
      render: (v: string, row) => (
        <Space size={4}>
          <span style={{ color: '#FFD06A' }}>{rupees(v)}</span>
          <Tag
            bordered={false}
            color={row.status === 'approved' ? 'green' : 'default'}
            style={{ fontSize: 10 }}
          >
            {row.status === 'approved' ? '✓ withheld' : 'withheld'}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Amount to pay',
      dataIndex: 'netPayoutPaise',
      render: (v: string) => <span style={{ fontWeight: 700 }}>{rupees(v)}</span>,
    },
    {
      title: 'Pay to',
      render: (_: unknown, row) => <PayToCell row={row} />,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: string) =>
        s === 'approved' ? (
          <Tag color="gold">Approved · pay now</Tag>
        ) : (
          <Tag>Pending review</Tag>
        ),
    },
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      render: (s: string) => new Date(s).toLocaleString(),
    },
    {
      title: 'Approved by',
      render: (_: unknown, row) =>
        row.status === 'approved' ? (
          <ActorCell actor={row.approvedByAdmin} at={row.approvedAt} testId={`approved-${row.id}`} />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Action',
      render: (_: unknown, row) => (
        <Space>
          {row.status === 'pending_approval' && (
            <Can permission="payouts.approve">
              <Popconfirm
                title="Approve this payout?"
                description="It moves to 'approved' and waits to be paid. The listener's balance is already held."
                okText="Approve"
                onConfirm={() => approve.mutate(row.id)}
              >
                <Button size="small" type="primary" loading={approve.isPending && approve.variables === row.id}>
                  Approve
                </Button>
              </Popconfirm>
            </Can>
          )}
          {row.status === 'approved' && (
            <Can permission="payouts.mark_paid">
              <Button size="small" type="primary" onClick={() => setPaying(row)}>
                Mark paid
              </Button>
            </Can>
          )}
          <Can permission="payouts.reject">
            <Button size="small" danger onClick={() => setRejecting(row)}>
              Reject
            </Button>
          </Can>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        Payouts
      </Typography.Title>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic title="Pending approvals" value={data?.length ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic title="Pending gross" value={rupees(total.toString())} valueStyle={{ color: '#FFD06A' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Can permission="payouts.reports.export">
            <Card>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text type="secondary">TDS report</Typography.Text>
                <Space.Compact style={{ width: '100%' }}>
                  <Input value={fy} onChange={(e) => setFy(e.target.value)} placeholder="2025-26" />
                  <Button type="primary" icon={<DownloadOutlined />} onClick={() => void downloadTdsCsv()}>
                    CSV
                  </Button>
                </Space.Compact>
              </Space>
            </Card>
          </Can>
        </Col>
      </Row>

      <Alert
        type="info"
        showIcon
        message="TDS is withheld once — at the listener's request. It's already inside “Gross” and removed from “Net”; the TDS shown on an Approved row is the same withholding, not a second deduction. Approval and “Mark paid” move no extra TDS."
      />

      <Card>
        <Tabs
          activeKey={tab}
          onChange={(k) => setSearchParams(k === 'history' ? { tab: 'history' } : {}, { replace: true })}
          items={[
            {
              key: 'queue',
              label: `Work queue (${data?.length ?? 0})`,
              children: (
                <>
                  <Space style={{ marginBottom: 16 }} wrap>
                    <Input.Search
                      allowClear
                      aria-label="Search payouts"
                      placeholder="Listener name or @handle"
                      onChange={(e) => setQ(e.target.value)}
                      style={{ minWidth: 280 }}
                    />
                    <Can permission="payouts.reports.export">
                      <Button
                        icon={<DownloadOutlined />}
                        loading={exportingQueue}
                        disabled={queue.length === 0}
                        onClick={() => void exportQueue()}
                      >
                        Export Excel
                      </Button>
                    </Can>
                  </Space>
                  <Table
                    rowKey="id"
                    loading={isLoading}
                    dataSource={queue}
                    columns={columns}
                    pagination={{ pageSize: 25, showSizeChanger: true }}
                    scroll={{ x: 'max-content' }}
                  />
                </>
              ),
            },
            { key: 'history', label: 'History', children: <PayoutHistory /> },
          ]}
        />
      </Card>

      <Modal
        title={rejecting ? `Reject payout · ${rupees(rejecting.amountPaise)}` : ''}
        open={!!rejecting}
        onCancel={() => setRejecting(null)}
        onOk={() => rejecting && reject.mutate({ id: rejecting.id, reason: rejectReason })}
        okButtonProps={{
          danger: true,
          disabled: rejectReason.trim().length < 10,
          loading: reject.isPending,
        }}
        okText="Reject + reverse"
      >
        <Typography.Paragraph type="secondary">
          The full amount (including TDS) is returned to the listener&apos;s wallet. Reason ≥ 10 chars.
        </Typography.Paragraph>
        <Input.TextArea
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>

      <Modal
        title={paying ? `Mark paid · ${rupees(paying.netPayoutPaise)}` : ''}
        open={!!paying}
        onCancel={() => setPaying(null)}
        onOk={() => paying && markPaid.mutate({ id: paying.id, paymentReference: payRef })}
        okButtonProps={{ loading: markPaid.isPending }}
        okText="Confirm paid"
      >
        <Typography.Paragraph type="secondary">
          Confirm you&apos;ve transferred{' '}
          <strong>{paying ? rupees(paying.netPayoutPaise) : ''}</strong> to{' '}
          <strong>
            {paying?.payoutMethod === 'upi'
              ? `${paying?.accountHolderName ?? 'UPI'} · ${paying?.upiId ?? ''}`
              : `${paying?.accountHolderName ?? ''} · ${paying?.bankName ?? 'bank not given'} · A/C ${paying?.accountNumber ?? ''} (${paying?.ifsc ?? ''})`}
          </strong>
          . Optionally record a payment reference (UTR / GPay / PhonePe txn id).
        </Typography.Paragraph>
        <Input
          value={payRef}
          onChange={(e) => setPayRef(e.target.value)}
          placeholder="Payment reference (optional)"
        />
      </Modal>
    </Space>
  );
}

function currentFy(): string {
  const d = new Date();
  const m = d.getUTCMonth();
  const y = d.getUTCFullYear();
  const start = m >= 3 ? y : y - 1;
  const end = (start + 1) % 100;
  return `${start}-${String(end).padStart(2, '0')}`;
}
